import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import { describeError } from "../../api/client";
import type { AdminCampaignPage, AdminCampaignSummary, AdminOverview } from "../../api/types";
import { RequireRole } from "../../auth/RequireRole";
import { DeliveryProgressBar } from "../../components/DeliveryProgressBar";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { formatDateTime } from "../../formatting/date";
import { formatCount, isValidCount } from "../../formatting/number";
import { DEFAULT_STALE_TIME_MS, invalidatePrefix } from "../../state/queryClient";
import { useQuery } from "../../state/useQuery";
import { listCampaigns } from "../campaigns/api";
import { CampaignCreatePanel } from "../campaigns/CampaignCreatePanel";
import { getOverview, getUpcomingScheduledCampaigns } from "./api";
import {
  compareScheduledDates,
  firstName,
  formatDeliveryRateFromMetrics,
  formatHeaderLine,
  formatMetricDisplay,
  formatSentRate,
  formatShortDateOrNone,
  formatTemplateShowMeta,
  formatTimeHHmm,
  formatUpcomingMeta,
  greetingForHour,
  isOverdueSchedule,
  isWithinNextSevenDays,
  metricValue,
  monthDayParts,
  relativeDayLabel,
} from "./derived";

/**
 * Native-review advisory i: restored from the old pre-rewrite
 * `AttentionLedger`'s own `renderValidatedCount(item.count, formatCount)`
 * call (dropped by the §4 rewrite) — `AdminPriorityItem.count` is a plain
 * `number` on the wire with no backend-side invariant enforcing
 * finiteness/non-negativity, and `formatCount` THROWS on NaN/Infinity rather
 * than rendering it, which would otherwise crash this whole screen instead
 * of degrading one row.
 */
function errorCountMeta(count: number): ReactNode {
  if (!isValidCount(count) || count < 0) {
    return <span className="v2-status v2-status--critical">Dato inválido</span>;
  }
  // Codex finding 2 (PR #382): a priority with `count: 0` names a campaign
  // fallida/parcial with no error EVENTS in the 7-day window — a structural
  // failure, not "zero errors" — so it must never read as a non-issue.
  if (count === 0) {
    return "Envío con problemas";
  }
  return `${formatCount(count)} ${count === 1 ? "error" : "errores"}`;
}

/**
 * Codex finding 1 (PR #382): `overview.priorities` is itself bounded at the
 * server by `overview.scope.priorityLimit` — once the returned list reaches
 * that limit, its own length is a floor, not a verified exact total, so
 * showing it as a precise count (e.g. "10") would misrepresent a possibly
 * much larger real count as exact. "10+" makes the boundedness visible.
 */
function priorityCountDisplay(priorityCount: number, priorityLimit: number): string | undefined {
  // Guard: a non-positive or invalid (NaN) priorityLimit is not a real
  // server-side bound to disclose — "0+" or "-1+" would misrepresent an
  // exact count as truncated. Only a valid, positive limit the list has
  // actually reached earns the "+" suffix.
  if (!Number.isFinite(priorityLimit) || priorityLimit <= 0) return undefined;
  return priorityCount >= priorityLimit ? `${priorityLimit}+` : undefined;
}

/**
 * Codex finding 1 (PR #382 review round 3): `getUpcomingScheduledCampaigns`'s
 * own bound can fall short of the server's real `total` on a pathological
 * account (finding 7's own `scheduledTruncated` flag) — once that happened,
 * `weekItems.length` is only a LOWER bound on "programadas esta semana", not
 * an exact count, so it must carry the same "+" marker `priorityCountDisplay`
 * already uses for a comparable truncation.
 */
function weekCountDisplay(weekCount: number, scheduledTruncated: boolean): string | undefined {
  return scheduledTruncated ? `${weekCount}+` : undefined;
}

/** `/campanas/:campaignId` — same real registered route every other screen's "open campaign" link targets. */
function campaignLinkTarget(campaignId: string): string {
  return `/campanas/${campaignId}`;
}

const ATTENTION_ROW_LIMIT = 3;
const UPCOMING_ROW_LIMIT = 4;
/** Max page size requested for the "Enviando ahora" live query (see the `listCampaigns` call below and its truncation note). */
const LIVE_PAGE_SIZE = 50;

/**
 * `/` — Inicio (REDISENO-PANEL.md §4, panel-redesign-v3 T3). Replaces the
 * previous "Resumen operativo" ledger pair (attention table + recent
 * campaigns table) with the redesigned triage dashboard: a time-of-day
 * greeting, "Requiere tu atención" (errores / borradores / programadas esta
 * semana), "Enviando ahora" (live, polled), a 7-day KPI card, "Próximos
 * envíos", and a welcome empty state for a brand-new account. The "Campañas
 * recientes" table is intentionally gone (§4: "desaparece de Inicio — la
 * lista completa está en Campañas").
 *
 * Sourced from `GET panel/overview` (unchanged endpoint) plus three
 * `listCampaigns` reads already used elsewhere in this codebase
 * (`estado=borrador|programada|enviando`) — no new endpoint, no client-side
 * total/sum invented beyond the explicit §9.4 allow-list (relative dates,
 * the greeting, the 7-day window, delivery rate, bar percentages).
 */
export function OverviewScreen() {
  const { accounts } = useMsal();
  const account = accounts[0];

  // Codex finding 2 (PR #382 review round 3): a long-lived Inicio session
  // had nothing to refresh this snapshot — the live "enviando" query keeps
  // polling every 10s while `overview.metrics`/`priorities`/lifecycle
  // counts/`generatedAt` stayed frozen, so a new delivery/error event could
  // update "Enviando ahora" while "Últimos 7 días"/"con errores de envío"
  // contradicted it indefinitely. Reuses the same documented 30s overview
  // stale-time (queryClient.ts's `DEFAULT_STALE_TIME_MS`) as the poll
  // cadence, same convention `pollIntervalMs` already follows elsewhere
  // (e.g. the live query below, AppShell's own "overview" poll for the
  // Campañas badge — same key, so either poll's fresh response is broadcast
  // to this instance too via `useQuery`'s cross-instance notify).
  const { data, error, loading, stale, refreshing, updatedAt, refetch } = useQuery<AdminOverview>({
    key: ["overview"],
    queryFn: getOverview,
    pollIntervalMs: DEFAULT_STALE_TIME_MS.overview,
  });

  // `null` while `overview` itself hasn't resolved yet — the three
  // `listCampaigns` reads below stay enabled (their default) until we
  // actually know the account has zero campaigns, so a slow-but-successful
  // overview fetch never races past already-started requests. `.some(...)`
  // over the already-server-counted `campaignStates` (never a client-side
  // sum/total — design.md/§9.4 "nada más se calcula en el navegador") is
  // enough to answer "does this account have any campaign at all".
  const hasCampaignsAnywhere = data ? data.campaignStates.some((state) => state.count > 0) : null;
  // Codex finding 7 (PR #382 review round 3): `hasCampaignsAnywhere` being
  // `null` was treated as "keep the child queries enabled" for BOTH "still
  // loading" and "conclusively failed with no data ever resolved" — the page
  // can only render the overview error in the latter case, but the live
  // query's 10s poll kept running behind it indefinitely, generating backend
  // traffic for a screen the operator cannot see or use. `error !==
  // undefined && data === undefined` is specifically the "will never resolve
  // on its own" case (useQuery's own `loading` is already false there) — a
  // later BACKGROUND refetch failure with data already on screen (`stale`)
  // must not disable these, so this only ever gates the true first-failure case.
  const overviewConclusivelyFailed = error !== undefined && data === undefined;
  const hasAnyCampaign =
    !overviewConclusivelyFailed && (hasCampaignsAnywhere === null || hasCampaignsAnywhere === true);

  const draftsQuery = useQuery<AdminCampaignPage>({
    key: ["campaigns", "borrador", "inicio"],
    queryFn: () => listCampaigns({ estado: "borrador", size: 50 }),
    enabled: hasAnyCampaign,
    // Codex round 6 (PR #382): same cadence as the overview poll so a
    // long-lived session does not keep stale draft rows/counts.
    pollIntervalMs: DEFAULT_STALE_TIME_MS.overview,
  });

  // Codex finding 7 (PR #382): a bare `listCampaigns({estado:"programada",
  // size:50})` only ever saw page 1 — `getUpcomingScheduledCampaigns` walks
  // as many pages as the account needs (bounded) so the nearest upcoming
  // sends are never silently missing past the first 50.
  const scheduledQuery = useQuery<AdminCampaignPage>({
    key: ["campaigns", "programada", "inicio"],
    queryFn: () => getUpcomingScheduledCampaigns(),
    enabled: hasAnyCampaign,
    // Codex round 6 (PR #382): poll alongside the overview so scheduled rows
    // created/cancelled by another operator do not stay frozen.
    pollIntervalMs: DEFAULT_STALE_TIME_MS.overview,
  });

  // §4 "Enviando ahora... con sondeo de 10 s (ya existe en la lista)" — same
  // `pollIntervalMs` convention `CampaignListScreen` already uses. Codex
  // finding 7 (PR #382, live query): the default page size (20) ignored
  // `total` — an account with more than 20 campaigns "enviando" at once
  // could silently miss some. Request the max size (`LIVE_PAGE_SIZE`);
  // `LiveSection` itself discloses when `total` still exceeds that.
  const liveQuery = useQuery<AdminCampaignPage>({
    key: ["campaigns", "enviando", "inicio"],
    queryFn: () => listCampaigns({ estado: "enviando", size: LIVE_PAGE_SIZE }),
    enabled: hasAnyCampaign,
    pollIntervalMs: 10_000,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const openCreate = () => setCreateOpen(true);
  const closeCreate = () => setCreateOpen(false);
  const handleCreated = () => {
    setCreateOpen(false);
    // Same invalidation convention `AppShell`'s own "+ Nueva campaña" uses
    // (odd/tasks/panel-redesign-v3.md T2 progress note): any mounted screen
    // watching either prefix (this screen's own three list reads, the
    // overview priority badge) refetches on its own.
    invalidatePrefix(["campaigns"]);
    invalidatePrefix(["overview"]);
  };

  return (
    <section aria-label="Inicio" className="v2-overview">
      {/* Codex finding 6 (PR #382 round 4): the real OverviewHeader below
          needs `data` for its meta timestamp line, but the <h1> itself must
          not disappear while data hasn't resolved yet — keyboard/screen-
          reader users navigating by headings need a stable Inicio heading
          through the initial loading AND a terminal first-error state, and
          the error screen needs its page title back. Degrades only the
          generated-timestamp line (omitted here, shown once `data` lands). */}
      {!data && <InicioFallbackHeading accountName={account?.name ?? account?.username} />}

      {loading && <p role="status">Cargando Inicio…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel variant="error" message={describeError(error, "No se pudo cargar Inicio.")} onRetry={refetch} />
      )}

      {data && (
        <>
          {stale && (
            // Codex finding 8 (PR #382): a Reintentar click while this exact
            // background refresh is already in flight gave no feedback and
            // allowed stacking a duplicate retry — "Actualizando…" + a
            // disabled button cover that one in-flight request.
            <FeedbackPanel
              variant="stale"
              message={
                refreshing
                  ? "Actualizando…"
                  : `Datos anteriores${updatedAt ? ` · ${formatDateTime(new Date(updatedAt).toISOString())}` : ""}`
              }
              onRetry={refetch}
              disabled={refreshing}
            />
          )}

          <OverviewHeader overview={data} accountName={account?.name ?? account?.username} onNuevaCampana={openCreate} />

          {hasCampaignsAnywhere === false ? (
            <WelcomeEmptyState onCrearCampana={openCreate} />
          ) : (
            <OverviewContent
              overview={data}
              draftsQuery={draftsQuery}
              scheduledQuery={scheduledQuery}
              liveQuery={liveQuery}
            />
          )}
        </>
      )}

      {createOpen && <CampaignCreatePanel onClose={closeCreate} onCreated={handleCreated} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Header (§4: fecha + "actualizado HH:MM", saludo, "+ Nueva campaña" de escritorio)
// ---------------------------------------------------------------------------

/**
 * Codex finding 6 (PR #382 round 4): a data-independent stand-in for
 * `OverviewHeader`'s own <h1>, rendered only while `data` hasn't resolved
 * (loading, or a terminal first-error state) — never alongside the real
 * header, so there's always exactly one <h1> on screen. Uses the real local
 * clock for the greeting instead of `overview.generatedAt` (unavailable
 * here); the meta timestamp line itself is the one thing that degrades away.
 */
function InicioFallbackHeading({ accountName }: { accountName: string | undefined }) {
  const greeting = greetingForHour(new Date().getHours());
  return (
    <header className="v2-overview__header">
      <div className="v2-overview__header-text">
        <h1>
          {greeting}, {firstName(accountName)}
        </h1>
      </div>
    </header>
  );
}

function OverviewHeader({
  overview,
  accountName,
  onNuevaCampana,
}: {
  overview: AdminOverview;
  accountName: string | undefined;
  onNuevaCampana: () => void;
}) {
  const greeting = greetingForHour(new Date(overview.generatedAt).getHours());
  return (
    <header className="v2-overview__header">
      <div className="v2-overview__header-text">
        <span className="v2-overview__header-meta">{formatHeaderLine(overview.generatedAt)}</span>
        <h1>
          {greeting}, {firstName(accountName)}
        </h1>
      </div>
      <RequireRole role="Operador">
        <button
          type="button"
          className="v2-btn-primary v2-u-target-min v2-overview__cta-desktop"
          onClick={onNuevaCampana}
        >
          + Nueva campaña
        </button>
      </RequireRole>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Welcome empty state (§4 item 4: "Todavía no tienes campañas")
// ---------------------------------------------------------------------------

function WelcomeEmptyState({ onCrearCampana }: { onCrearCampana: () => void }) {
  return (
    <section aria-label="Todavía no tienes campañas" className="v2-overview__welcome">
      <h2>Todavía no tienes campañas</h2>
      <p>
        Importa tus contactos, elige una plantilla y crea tu primera campaña. Aquí verás lo que necesita tu atención
        cada día.
      </p>
      <div className="v2-overview__welcome-actions">
        {/* Codex finding 11 (PR #382): "Importar contactos" starts a write
            flow (`ContactImportPanel` itself is RequireRole role="Operador"
            gated) — same convention every other write action on this screen
            already follows. */}
        <RequireRole role="Operador">
          <Link to="/contactos" className="v3-btn-secondary v2-u-target-min">
            Importar contactos
          </Link>
        </RequireRole>
        <RequireRole role="Operador">
          <button type="button" className="v2-btn-primary v2-u-target-min" onClick={onCrearCampana}>
            Crear campaña
          </button>
        </RequireRole>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard content (§4 items 1-3, shown whenever the account has at
// least one campaign anywhere in its lifecycle).
// ---------------------------------------------------------------------------

type CampaignsQueryResult = ReturnType<typeof useQuery<AdminCampaignPage>>;

function OverviewContent({
  overview,
  draftsQuery,
  scheduledQuery,
  liveQuery,
}: {
  overview: AdminOverview;
  draftsQuery: CampaignsQueryResult;
  scheduledQuery: CampaignsQueryResult;
  liveQuery: CampaignsQueryResult;
}) {
  const now = Date.now();
  // Native-review advisory o: `compareScheduledDates` never returns NaN (an
  // invalid `fechaProgramada` always sorts last), unlike a bare
  // `getTime() - getTime()` subtraction.
  const scheduledItemsAll = [...(scheduledQuery.data?.items ?? [])].sort((a, b) =>
    compareScheduledDates(a.fechaProgramada, b.fechaProgramada),
  );
  const liveItems = liveQuery.data?.items ?? [];
  // Codex finding 3 (PR #382): the live snapshot can resolve just before the
  // scheduled snapshot reflects the same campaign's transition out of
  // "programada" — showing it in BOTH "programadas esta semana"/"Próximos
  // envíos" AND "Enviando ahora" at once. Excluding any id already confirmed
  // live is a pure client-side filter, applied on every render (not just a
  // later refetch) — it fixes the race on the very FIRST live resolution too,
  // in addition to the id-set-change refetch effect below (which only
  // handles a LATER change).
  const liveIdSet = new Set(liveItems.map((campaign) => campaign.campaignId));
  const scheduledItems = scheduledItemsAll.filter((campaign) => !liveIdSet.has(campaign.campaignId));
  const weekItems = scheduledItems.filter((campaign) => isWithinNextSevenDays(campaign.fechaProgramada, now));
  // Codex finding 1 (PR #382 round 4): scheduledItems sorts ascending by
  // date, so once at least UPCOMING_ROW_LIMIT campaigns are overdue, they
  // occupy every slot this slice has — excluding overdue rows first keeps
  // "Próximos envíos" limited to genuinely future sends (an overdue row is
  // still surfaced in "Requiere tu atención" via weekItems above, which
  // intentionally has no lower bound).
  const upcomingItems = scheduledItems
    .filter((campaign) => !isOverdueSchedule(campaign.fechaProgramada, now))
    .slice(0, UPCOMING_ROW_LIMIT);
  // Codex finding 7 (PR #382): `getUpcomingScheduledCampaigns`'s own bound
  // (10 pages / 500 rows) can still fall short of `total` on a pathological
  // account — surface that instead of silently showing an incomplete set as
  // if it were everything. Compared against the UNFILTERED set: the
  // live-exclusion filter above is a display concern, not a measure of
  // whether the bounded fetch itself reached the server's real total.
  const scheduledTruncated = scheduledItemsAll.length < (scheduledQuery.data?.total ?? 0);

  // Codex finding 13 (PR #382): a campaign leaving "programada" by starting
  // to send would otherwise leave this screen's "programadas esta semana" /
  // "Próximos envíos" counts stale until the next full 10s live poll happens
  // to coincide with a re-render that re-reads scheduledQuery — refetch
  // "programadas" directly whenever the live query's own SET of campaign ids
  // changes (sorted + joined so reordering the same set is a no-op).
  const idsKey = liveItems.map((campaign) => campaign.campaignId).sort().join(",");
  // Codex round 6 (PR #382): a truncated live page (total > rows) is NOT a
  // complete id set, so an unchanged key cannot prove the scheduled snapshot
  // is current — fold the live resolution timestamp into the key so every
  // live poll while truncated also refetches "programadas".
  const liveTruncated = (liveQuery.data?.total ?? 0) > liveItems.length;
  const liveIdsKey = liveTruncated ? `${idsKey}|truncated@${liveQuery.updatedAt ?? ""}` : idsKey;
  // Native-review advisory m: `liveIdsKey` reads as `""` BEFORE the live
  // query has resolved even once (liveQuery.data is still undefined), same
  // as a genuinely resolved "zero live campaigns" baseline would. Gating the
  // whole effect on `liveHasResolved` means the ref is only ever seeded from
  // a REAL resolution, so the live query's very first resolve (empty →
  // actual ids) is treated as establishing the baseline, not as a change to
  // diff against — the spurious refetch this used to fire on every mount.
  const liveHasResolved = liveQuery.data !== undefined;
  const refetchScheduled = scheduledQuery.refetch;
  const previousLiveIdsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!liveHasResolved) return;
    const previous = previousLiveIdsKeyRef.current;
    if (previous !== null && previous !== liveIdsKey) {
      refetchScheduled();
    }
    previousLiveIdsKeyRef.current = liveIdsKey;
  }, [liveHasResolved, liveIdsKey, refetchScheduled]);

  return (
    <>
      <AttentionSection
        overview={overview}
        draftsQuery={draftsQuery}
        scheduledLoading={scheduledQuery.loading}
        scheduledRefreshing={scheduledQuery.refreshing}
        scheduledError={scheduledQuery.error}
        onRetryScheduled={scheduledQuery.refetch}
        weekItems={weekItems}
        weekTruncated={scheduledTruncated}
        // Codex finding 5 (PR #382 round 4): `weekItems.length` is empty
        // both while the scheduled query is pending/failed-before-resolving
        // AND once it has genuinely resolved to zero — only
        // `scheduledQuery.data` existing confirms the latter.
        weekCountUnavailable={scheduledQuery.data === undefined}
        now={now}
      />

      {(liveItems.length > 0 || liveQuery.loading || liveQuery.error !== undefined) && (
        <LiveSection
          items={liveItems}
          total={liveQuery.data?.total ?? liveItems.length}
          loading={liveQuery.loading}
          error={liveQuery.error}
          onRetry={liveQuery.refetch}
        />
      )}

      <div className="v2-overview__row">
        <WeekSummaryCard metrics={overview.metrics} />
        <UpcomingCard
          items={upcomingItems}
          now={now}
          truncated={scheduledTruncated}
          resolved={scheduledQuery.data !== undefined}
          loading={scheduledQuery.loading}
          refreshing={scheduledQuery.refreshing}
          error={scheduledQuery.error}
          onRetry={scheduledQuery.refetch}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 1. "Requiere tu atención" (§4 item 1)
// ---------------------------------------------------------------------------

interface AttentionRow {
  key: string;
  name: string;
  meta: ReactNode;
  metaTone?: "critical" | "accent";
  to?: string;
}

function AttentionSection({
  overview,
  draftsQuery,
  scheduledLoading,
  scheduledRefreshing,
  scheduledError,
  onRetryScheduled,
  weekItems,
  weekTruncated,
  weekCountUnavailable,
  now,
}: {
  overview: AdminOverview;
  draftsQuery: CampaignsQueryResult;
  scheduledLoading: boolean;
  /** Codex finding (PR #382 round 5): mirrors `draftsQuery.refreshing` — the "programadas esta semana" stale banner must disable on a background refresh in flight, not on `scheduledLoading` (false once items have loaded). */
  scheduledRefreshing: boolean | undefined;
  scheduledError: unknown;
  onRetryScheduled: () => void;
  weekItems: readonly AdminCampaignSummary[];
  weekTruncated: boolean;
  /** Codex finding 5 (PR #382 round 4): renders "—" instead of the count while the scheduled query has never resolved — `0` is reserved for a confirmed empty result, same convention `AttentionCard` already applies to drafts. */
  weekCountUnavailable: boolean;
  now: number;
}) {
  const errorRows: AttentionRow[] = overview.priorities.slice(0, ATTENTION_ROW_LIMIT).map((item, index) => ({
    key: `${item.kind}-${item.campaignId ?? index}`,
    name: item.campaignName ?? "—",
    meta: errorCountMeta(item.count),
    metaTone: "critical",
    to: item.campaignId ? campaignLinkTarget(item.campaignId) : undefined,
  }));

  const draftItems = draftsQuery.data?.items ?? [];
  const draftRows: AttentionRow[] = draftItems.slice(0, ATTENTION_ROW_LIMIT).map((campaign) => ({
    key: campaign.campaignId,
    name: campaign.nombre,
    meta: formatShortDateOrNone(campaign.fechaProgramada),
    to: campaignLinkTarget(campaign.campaignId),
  }));

  const weekRows: AttentionRow[] = weekItems.slice(0, ATTENTION_ROW_LIMIT).map((campaign) => ({
    key: campaign.campaignId,
    name: campaign.nombre,
    meta: relativeDayLabel(campaign.fechaProgramada, now),
    metaTone: "accent",
    to: campaignLinkTarget(campaign.campaignId),
  }));

  return (
    <section aria-label="Requiere tu atención" className="v2-overview__attention">
      <div className="v2-overview__attention-grid">
        <AttentionCard
          title="con errores de envío"
          count={overview.priorities.length}
          displayCount={priorityCountDisplay(overview.priorities.length, overview.scope.priorityLimit)}
          tone={overview.priorities.length > 0 ? "critical" : "faint"}
          emptyText="Ningún envío reciente tuvo errores."
          rows={errorRows}
        />
        <AttentionCard
          title="borradores por programar"
          count={draftsQuery.data?.total ?? 0}
          // Codex finding 4 (PR #382 review round 3): while the drafts
          // request is pending, or has failed before ever resolving, the
          // count above is not a confirmed answer — `0` would misrepresent
          // "unknown" as a measured empty result.
          countUnavailable={draftsQuery.data === undefined}
          tone={(draftsQuery.data?.total ?? 0) > 0 ? "warning" : "faint"}
          emptyText="No tienes borradores pendientes."
          rows={draftRows}
          resolved={draftsQuery.data !== undefined}
          loading={draftsQuery.loading}
          refreshing={draftsQuery.refreshing}
          error={draftsQuery.error}
          onRetry={draftsQuery.refetch}
        />
        <AttentionCard
          title="programadas esta semana"
          count={weekItems.length}
          displayCount={weekCountDisplay(weekItems.length, weekTruncated)}
          countUnavailable={weekCountUnavailable}
          tone={weekItems.length > 0 ? "accent" : "faint"}
          emptyText="Nada programado para los próximos 7 días."
          rows={weekRows}
          resolved={!weekCountUnavailable}
          loading={scheduledLoading}
          refreshing={scheduledRefreshing}
          error={scheduledError}
          onRetry={onRetryScheduled}
        />
      </div>
    </section>
  );
}

function AttentionCard({
  title,
  count,
  displayCount,
  countUnavailable,
  tone,
  emptyText,
  rows,
  resolved,
  loading,
  refreshing,
  error,
  onRetry,
}: {
  title: string;
  count: number;
  /** Codex finding 1 (PR #382): overrides the formatted `count` text (e.g. "10+") without changing `tone`'s own numeric basis. */
  displayCount?: string;
  /** Codex finding 4 (PR #382 review round 3): renders "—" instead of `displayCount`/`count` while the real answer is not yet known (no data has ever resolved) — `0` is reserved for a confirmed empty response. */
  countUnavailable?: boolean;
  tone: "critical" | "warning" | "accent" | "faint";
  emptyText: string;
  rows: readonly AttentionRow[];
  /** Codex round 6 (PR #382): the query has resolved at least once (possibly to an empty page) — a later failed refetch keeps the confirmed result with a stale note instead of a full error. Defaults to "has rows". */
  resolved?: boolean;
  loading?: boolean;
  /** Codex finding (PR #382 round 5): mirrors `UpcomingCard`'s `refreshing` — the stale banner's Reintentar must disable on a background refresh in flight, not on `loading` (which is false once items have loaded). */
  refreshing?: boolean;
  error?: unknown;
  onRetry?: () => void;
}) {
  // Native-review advisory n: a failed BACKGROUND refetch must not replace
  // already-loaded rows with the full error panel — mirror LiveSection's
  // `hasItems` gating so the full error/loading states are reserved for
  // "nothing to show yet", and a later failure with data already on screen
  // degrades to a small inline note instead (applies to "programadas esta
  // semana" and "borradores por programar", which share this card).
  const hasItems = resolved ?? rows.length > 0;
  return (
    <article className="v2-overview__attention-card">
      <div className="v2-overview__attention-card-head">
        <span className={`v2-overview__attention-count v2-overview__attention-count--${tone}`}>
          {countUnavailable ? "—" : (displayCount ?? formatCount(count))}
        </span>
        <span className="v2-overview__attention-card-title">{title}</span>
      </div>
      {!hasItems && error !== undefined ? (
        // Codex finding (PR #382 round 5): after an initial failure,
        // clicking Reintentar sets `loading` true but keeps the previous
        // `error`, so this branch stays mounted for the whole retry —
        // disable the button while loading, same `disabled={loading}`
        // convention `UpcomingCard` already uses, since neither `draftsQuery`
        // nor `scheduledQuery` forwards an abort signal to `listCampaigns`.
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudo cargar esta información.")}
          onRetry={onRetry}
          disabled={loading}
        />
      ) : !hasItems && loading ? (
        // Native-review advisory l: `rows` is [] both while the query is
        // still loading AND once it has genuinely resolved to empty — this
        // card must not assert `emptyText` before the real answer is known.
        <p role="status">Cargando…</p>
      ) : (
        <>
          {error !== undefined && (
            // Codex finding (PR #382 round 5): once items have loaded,
            // `loading` stays false by `useQuery`'s contract even while a
            // background refresh/retry is in flight — `refreshing` is the
            // flag that reflects that, same distinction `UpcomingCard`
            // already makes for its own stale banner.
            <FeedbackPanel
              variant="stale"
              message="No se pudo actualizar."
              onRetry={onRetry}
              disabled={refreshing}
            />
          )}
          {rows.length === 0 ? (
            <p className="v2-overview__attention-empty">{emptyText}</p>
          ) : (
            <ul className="v2-overview__attention-rows">
              {rows.map((row) => (
                <li key={row.key}>
                  {row.to ? (
                    <Link to={row.to} className="v2-overview__attention-row">
                      {/* Codex finding 13 (PR #382 review round 3): the CSS
                          ellipsizes this span when the name exceeds the
                          card's width, but nothing disclosed the full value
                          — a sighted user with several similarly-prefixed
                          campaigns could not tell which one this link opens
                          without visiting its detail page. `title` is the
                          lightest-weight native hover/focus disclosure. */}
                      <span className="v2-overview__attention-row-name" title={row.name}>
                        {row.name}
                      </span>
                      <span
                        className={`v2-overview__attention-row-meta${row.metaTone ? ` v2-overview__attention-row-meta--${row.metaTone}` : ""}`}
                      >
                        {row.meta}
                      </span>
                    </Link>
                  ) : (
                    <span className="v2-overview__attention-row v2-overview__attention-row--static">
                      <span className="v2-overview__attention-row-name" title={row.name}>
                        {row.name}
                      </span>
                      <span className="v2-overview__attention-row-meta">{row.meta}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// 2. "Enviando ahora" (§4 item 2)
// ---------------------------------------------------------------------------

function LiveSection({
  items,
  total,
  loading,
  error,
  onRetry,
}: {
  items: readonly AdminCampaignSummary[];
  /** Codex finding 7 (PR #382, live query): the server-reported total — can exceed `items.length` once an account has more than the 50-row page size "enviando" at once. */
  total: number;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  // Native-review advisory k: a background poll error (or a forced refetch)
  // must never replace already-loaded items with the full error/loading
  // state — `useQuery` itself keeps the previous `data` on a failed refresh
  // precisely so the UI can do this. "Cargando…" is reserved for the very
  // first load (no items yet); a later failure degrades to a small inline
  // note next to the still-visible list, the same "keep the data, flag it"
  // discipline this screen's own top-level "Datos anteriores" banner uses.
  const hasItems = items.length > 0;
  const liveTruncated = total > items.length;
  return (
    <section aria-label="Enviando ahora" className="v2-overview__live">
      <div className="v2-overview__live-head">
        <span className="v2-overview__live-dot" aria-hidden="true" />
        <h2>Enviando ahora</h2>
      </div>
      {!hasItems && error !== undefined ? (
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudo cargar los envíos en curso.")}
          onRetry={onRetry}
        />
      ) : !hasItems && loading ? (
        <p role="status">Cargando envíos en curso…</p>
      ) : (
        <>
          {error !== undefined && <FeedbackPanel variant="stale" message="No se pudo actualizar." onRetry={onRetry} />}
          {/* Codex finding 7 (PR #382, live query): disclose when the page
              size (`LIVE_PAGE_SIZE`) couldn't fit every campaign currently "enviando". */}
          {liveTruncated && (
            <p className="v2-overview__live-truncated-note">
              Mostrando {formatCount(items.length)} de {formatCount(total)} ·{" "}
              <Link to="/campanas?estado=enviando">Ver todas →</Link>
            </p>
          )}
          <ul className="v2-overview__live-list">
          {items.map((campaign) => {
            // progress-bar-sent: the bar follows the SENDING pace (enviados =
            // distinct recipients ACS accepted), never delivery confirmations.
            // `?? 0` is only a numeric input for the bar's geometry; the TEXT
            // says "—" when `enviados` is missing (older API), never a
            // fabricated "0 de N" (§9.2), and the bar hides from assistive tech.
            const enviadosRaw = isValidCount(campaign.enviados) ? campaign.enviados : null;
            const sent = enviadosRaw ?? 0;
            // Named distinctly from `LiveSection`'s own `total` prop (the
            // server-reported total across ALL live campaigns) — this is one
            // campaign's own 'encolado' count, a different number entirely.
            const queuedRaw = metricValue(campaign.monitoring, "encolado");
            const queuedTotal = queuedRaw ?? 0;
            const errorCount = metricValue(campaign.monitoring, "error") ?? 0;
            const sentAvailable = enviadosRaw !== null;
            const sentDisplay = enviadosRaw === null ? "—" : formatCount(enviadosRaw);
            const totalDisplay = formatMetricDisplay(campaign.monitoring, "encolado");
            const sentRate = formatSentRate(enviadosRaw, queuedRaw);
            return (
              <li key={campaign.campaignId}>
                <Link to={campaignLinkTarget(campaign.campaignId)} className="v2-overview__live-row">
                  <span className="v2-overview__live-name-block">
                    {/* Codex finding 4 (PR #382 round 4): the CSS now
                        ellipsizes this span on narrow screens — `title` is
                        the same lightest-weight native disclosure the
                        attention-row names already use. */}
                    <span className="v2-overview__live-name" title={campaign.nombre}>
                      {campaign.nombre}
                    </span>
                    <span className="v2-overview__live-meta">
                      {formatTemplateShowMeta(campaign.templateName, campaign.showName)}
                    </span>
                  </span>
                  <span className="v2-overview__live-progress">
                    <span className="v2-overview__live-progress-line">
                      <span>
                        <strong>{sentDisplay}</strong> de {totalDisplay} enviados
                        {/* Codex finding (PR #382 round 5): a nonzero error
                            count was disclosed only through the progress
                            bar's red segment — its aria-label serves screen
                            readers, but a sighted user with a color-vision
                            deficiency got no text or symbol identifying the
                            failure portion. */}
                        {errorCount > 0 && (
                          <span className="v2-overview__live-progress-errors">
                            {" "}
                            · {errorCount} {errorCount === 1 ? "error" : "errores"}
                          </span>
                        )}
                      </span>
                      <span className="v2-overview__live-progress-pct">
                        {sentRate === "—" ? sentRate : `${sentRate} enviado`}
                      </span>
                    </span>
                    <DeliveryProgressBar
                      delivered={sent}
                      errorCount={errorCount}
                      total={queuedTotal}
                      height={8}
                      deliveredAvailable={sentAvailable}
                      countLabel="enviados"
                      active={campaign.estado === "enviando"}
                    />
                  </span>
                </Link>
              </li>
            );
          })}
          </ul>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 3. Row of 2 cards: "Últimos 7 días" + "Próximos envíos" (§4 item 3)
// ---------------------------------------------------------------------------

function WeekSummaryCard({ metrics }: { metrics: AdminOverview["metrics"] }) {
  const errorValue = metricValue(metrics, "error");
  const deliveryRate = formatDeliveryRateFromMetrics(metrics);
  return (
    <section aria-label="Últimos 7 días" className="v2-overview__card">
      <div className="v2-overview__card-head">
        <h2>Últimos 7 días</h2>
        <Link to="/telemetria">Ver telemetría →</Link>
      </div>
      <div className="v2-overview__kpi-grid">
        <KpiCell label="Enviados" value={formatMetricDisplay(metrics, "encolado")} />
        <KpiCell label="Entregados" value={formatMetricDisplay(metrics, "entregado")} />
        <KpiCell label="Errores" value={formatMetricDisplay(metrics, "error")} tone={errorValue && errorValue > 0 ? "critical" : undefined} />
        <KpiCell
          label="Tasa de entrega"
          value={deliveryRate}
          // Codex finding 14 (PR #382 review round 3): the positive (green)
          // tone was unconditional — when `encolado` is zero, missing, or
          // unavailable, `formatDeliveryRateFromMetrics` returns "—", and
          // coloring that unavailable result the same as a real rate makes
          // a common no-send state look like a successful delivery.
          tone={deliveryRate !== "—" ? "positive" : undefined}
        />
      </div>
    </section>
  );
}

function KpiCell({ label, value, tone }: { label: string; value: string; tone?: "critical" | "positive" }) {
  return (
    <div className="v2-overview__kpi">
      <span className="v2-overview__kpi-label">{label}</span>
      <span className={`v2-overview__kpi-value${tone ? ` v2-overview__kpi-value--${tone}` : ""}`}>{value}</span>
    </div>
  );
}

function UpcomingCard({
  items,
  now,
  truncated,
  resolved,
  loading,
  refreshing,
  error,
  onRetry,
}: {
  items: readonly AdminCampaignSummary[];
  now: number;
  truncated: boolean;
  /** Codex round 6 (PR #382): scheduled data has resolved at least once (possibly empty). */
  resolved: boolean;
  loading: boolean;
  /** Codex finding 2 (PR #382 round 4): once items have loaded, `loading` stays false by useQuery's own contract even while a background refresh/retry is in flight — that state is `refreshing` instead. */
  refreshing: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const hasItems = resolved;
  return (
    <section aria-label="Próximos envíos" className="v2-overview__card">
      <div className="v2-overview__card-head">
        <h2>Próximos envíos</h2>
        <Link to="/campanas">Ver campañas →</Link>
      </div>
      {/* Native-review advisory l: this card must receive the scheduled
          query's own loading/error — `items` is [] both while the query is
          pending AND once it failed, so without these it falsely asserted
          "No hay envíos programados." before the real answer was known.
          Native-review advisory n: a failed BACKGROUND refetch must not
          replace already-loaded rows with the full error panel — mirror
          LiveSection's `hasItems` gating so the full error/loading states
          are reserved for "nothing to show yet", and a later failure with
          data already on screen degrades to a small inline note instead. */}
      {!hasItems && error !== undefined ? (
        // Codex finding 9 (PR #382 review round 3): after an initial
        // failure, clicking Reintentar sets `loading` true but `error`
        // keeps its previous value until the retry settles — this branch
        // stays active (not the "Cargando…" one below it) for the whole
        // retry, so an undisabled Reintentar let every extra click
        // force-start another concurrent page walk (this paginated fetch
        // forwards no abort signal to `listCampaigns`, so nothing here
        // cancels an earlier one).
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudieron cargar los próximos envíos.")}
          onRetry={onRetry}
          disabled={loading}
        />
      ) : !hasItems && loading ? (
        <p role="status">Cargando próximos envíos…</p>
      ) : (
        <>
          {error !== undefined && (
            // Codex finding 2 (PR #382 round 4): `loading` is always false
            // here (items already loaded) — `refreshing` is the flag that
            // actually reflects a background refresh/retry in flight, same
            // distinction the top-level overview stale banner already makes.
            <FeedbackPanel variant="stale" message="No se pudo actualizar." onRetry={onRetry} disabled={refreshing} />
          )}
          {/* Codex finding 7 (PR #382): disclose when the bounded fetch
              behind this list couldn't load every programada row for this
              account. */}
          {truncated && (
            <p className="v2-overview__upcoming-truncated-note">
              No se pudieron cargar todas las campañas programadas — mostrando las más próximas.
            </p>
          )}
          {items.length === 0 ? (
            // Codex finding 5 (PR #382): --v2-color-ink-faint is only
            // ~2.56:1 on this card's white surface, below WCAG AA's 4.5:1
            // floor — this overview-scoped class uses --v2-color-ink-subtle
            // instead (overviewCss.test.ts verifies the real contrast ratio).
            // Codex finding (PR #382 round 5): when the scheduled query
            // succeeds but every 'programada' campaign is overdue,
            // `upcomingItems` (filtered above) is empty even though those
            // SAME campaigns can simultaneously be listed by "Requiere tu
            // atención" as still scheduled — "no hay envíos programados"
            // denied their existence. This card only ever claims there are
            // no UPCOMING sends, never that nothing is scheduled at all.
            <p className="v2-overview__upcoming-empty">No hay próximos envíos.</p>
          ) : (
            <ul className="v2-overview__upcoming-list">
              {items.map((campaign) => {
                const { month, day } = monthDayParts(campaign.fechaProgramada);
                const meta = formatUpcomingMeta(
                  formatTimeHHmm(campaign.fechaProgramada),
                  relativeDayLabel(campaign.fechaProgramada, now),
                  campaign.showName,
                );
                return (
                  <li key={campaign.campaignId}>
                    <Link to={campaignLinkTarget(campaign.campaignId)} className="v2-overview__upcoming-row">
                      <span className="v2-overview__upcoming-date">
                        <span className="v2-overview__upcoming-month">{month}</span>
                        <span className="v2-overview__upcoming-day">{day}</span>
                      </span>
                      <span className="v2-overview__upcoming-text">
                        <span className="v2-overview__upcoming-name" title={campaign.nombre}>
                          {campaign.nombre}
                        </span>
                        <span className="v2-overview__upcoming-meta" title={meta}>
                          {meta}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
